/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

// src/utils/reportWebVitals.ts

/**
 * Report web vitals and performance metrics to monitoring tools.
 *
 * Note on Sentry: web vitals are NOT sent to Sentry from here. Sentry's
 * browserTracingIntegration() (enabled in main.tsx) already captures Core Web
 * Vitals automatically as measurements on the pageload transaction — which is
 * the documented approach and powers the Sentry Web Vitals dashboard. Sending
 * an additional captureMessage() per metric per pageload would duplicate that
 * data and consume the error-event quota, pushing out real errors. So this
 * function only handles the dev console log and the GA4 event (which Sentry
 * tracing does not cover).
 *
 * @param name - The name of the metric (e.g., FCP, LCP, CLS)
 * @param value - The value of the metric (in milliseconds)
 */
export function reportWebVitals(name: string, value: number) {
  // Log metrics in development (for debugging)
  if (import.meta.env.DEV) {
    console.info("[Performance]", { name, value });
  }

  // Send to Google Analytics if configured. This is the non-redundant sink —
  // it lets the team track Core Web Vitals trends in GA4 alongside Sentry.
  if (import.meta.env.PROD && window.gtag) {
    window.gtag("event", `perf_${name}`, {
      event_category: "Performance",
      event_label: name,
      value: Math.round(value),
      non_interaction: true,
    });
  }
}
